import React, {
    createContext,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    getReactToken,
    JWTResponseDTO,
    logoutSession,
    validateToken,
} from '../api/auth.api';
import { setAuthToken } from '../providers/authTokenProvider';
import { getEnvironmentVariable } from '../utils';
import { useError } from './error-context';

type AuthContextType = {
    tokens: {
        sessionToken: string;
        reactToken: string;
    };
    setSessionToken: (token: string) => void;
    setUserInfo: React.Dispatch<
        SetStateAction<JWTResponseDTO['user'] | null>
    >;
    isAuthenticated: boolean;
    isAuthLoading: boolean;
    userInfo: JWTResponseDTO['user'] | null;
    setSessionTokenLoading:
        React.Dispatch<React.SetStateAction<boolean>>;
    logout: () => void;
    goToPlatform: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(
    undefined,
);

const platformURL = getEnvironmentVariable(
    'REACT_APP_PLATFORM_URL',
);

const LOGIN_URL = `${platformURL}/login`;
const ADMIN_URL = `${platformURL}/workspace`;

export const AuthProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const { showError } = useError();

    const [ tokens, setTokens ] = useState({
        reactToken: '',
        sessionToken: '',
    });

    const [
        sessionTokenLoading,
        setSessionTokenLoading,
    ] = useState(true);

    const [
        reactTokenLoading,
        setReactTokenLoading,
    ] = useState(true);

    const [ userInfo, setUserInfo ] = useState<
        JWTResponseDTO['user'] | null
    >(null);

    const isInitiated = useRef(false);

    const setSessionToken = useCallback((
        sessionToken: string,
    ) => {
        /*
         * This updates the Axios token provider synchronously
         * before sessionTokenLoading becomes false.
         */
        setAuthToken('sessionToken', sessionToken);

        setTokens(currentTokens => ({
            ...currentTokens,
            sessionToken,
        }));
    }, []);

    const resetReactInfo = useCallback(() => {
        localStorage.removeItem('reactToken');
        setAuthToken('reactToken', '');

        setTokens(currentTokens => ({
            reactToken: '',
            sessionToken: currentTokens.sessionToken,
        }));
    }, []);

    const resetSessionInfo = useCallback(() => {
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('user');

        setAuthToken('sessionToken', '');

        setTokens(currentTokens => ({
            reactToken: currentTokens.reactToken,
            sessionToken: '',
        }));

        setUserInfo(null);
    }, []);

    useEffect(() => {
        if (isInitiated.current) {
            return;
        }

        isInitiated.current = true;

        const reactToken =
            localStorage.getItem('reactToken') || '';

        const sessionToken =
            localStorage.getItem('sessionToken') || '';

        let storedUser: JWTResponseDTO['user'] | null = null;

        try {
            const userValue = localStorage.getItem('user');

            if (userValue) {
                storedUser = JSON.parse(userValue);
            }
        } catch {
            localStorage.removeItem('user');
        }

        const tokenChecks: Promise<unknown>[] = [];

        if (sessionToken && storedUser) {
            tokenChecks.push(
                validateToken(
                    sessionToken,
                    'AuthorizationUser',
                )
                .then((isValid) => {
                    if (!isValid) {
                        resetSessionInfo();
                        return;
                    }

                    setAuthToken(
                        'sessionToken',
                        sessionToken,
                    );

                    setTokens(currentTokens => ({
                        ...currentTokens,
                        sessionToken,
                    }));

                    setUserInfo(storedUser);
                })
                .catch((error) => {
                    const statusCode =
                        error?.response?.status ??
                        error?.status;

                    if (statusCode === 401) {
                        resetSessionInfo();
                        return;
                    }

                    showError({
                        code: statusCode || 500,
                        message: 'API Error',
                        description:
                            'Failed to validate session token',
                        showRefresh: true,
                    });
                }),
            );
        } else {
            resetSessionInfo();
        }

        if (reactToken) {
            tokenChecks.push(
                validateToken(reactToken)
                .then((isValid) => {
                    if (!isValid) {
                        resetReactInfo();
                        return;
                    }

                    setAuthToken(
                        'reactToken',
                        reactToken,
                    );

                    setTokens(currentTokens => ({
                        ...currentTokens,
                        reactToken,
                    }));
                })
                .catch((error) => {
                    const statusCode =
                        error?.response?.status ??
                        error?.status;

                    if (statusCode === 401) {
                        resetReactInfo();
                        window.location.reload();
                        return;
                    }

                    showError({
                        code: statusCode || 500,
                        message: 'API Error',
                        description:
                            'Failed to validate react token',
                        showRefresh: true,
                    });
                }),
            );
        } else {
            tokenChecks.push(
                getReactToken()
                .then(({ token }) => {
                    localStorage.setItem(
                        'reactToken',
                        token,
                    );

                    setAuthToken(
                        'reactToken',
                        token,
                    );

                    setTokens(currentTokens => ({
                        ...currentTokens,
                        reactToken: token,
                    }));
                })
                .catch((error) => {
                    showError({
                        code:
                            error?.response?.status ??
                            error?.status ??
                            500,
                        message: 'API Error',
                        description:
                            'Failed to get react token',
                        showRefresh: true,
                    });

                    resetReactInfo();
                }),
            );
        }

        /*
         * SessionTokenIframe controls sessionTokenLoading.
         * This context controls only reactTokenLoading here.
         */
        Promise.all(tokenChecks).finally(() => {
            setReactTokenLoading(false);
        });
    }, [
        resetReactInfo,
        resetSessionInfo,
        showError,
    ]);

    const onLogout = useCallback(() => {
        logoutSession().finally(() => {
            resetSessionInfo();
            window.location.href = LOGIN_URL;
        });
    }, [ resetSessionInfo ]);

    const onGoToPlatform = () => {
        window.open(ADMIN_URL, '_blank');
    };

    return (
        <AuthContext.Provider
            value={{
                tokens,
                setSessionToken,
                isAuthenticated: Boolean(tokens.reactToken),
                isAuthLoading:
                    sessionTokenLoading ||
                    reactTokenLoading,
                userInfo,
                setUserInfo,
                setSessionTokenLoading,
                logout: onLogout,
                goToPlatform: onGoToPlatform,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error(
            'useAuth must be used within AuthProvider',
        );
    }

    return context;
};